import { useEffect, useState } from "react";
import { getPublishedProjects } from "../lib/api";
import { useSite } from "../lib/site";
import { EditableText } from "../components/EditableText";
import { Seo } from "../components/Seo";
import { ProjectCard } from "../components/ProjectCard";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { site } = useSite();

  useEffect(() => {
    getPublishedProjects()
      .then((data) => {
        setProjects(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  return (
    <div data-testid="projects-page">
      <Seo
        title="Projects — oVu Cyber"
        description="Published cybersecurity projects by oVu Cyber — Python tools, Linux hardening scripts and CTF helpers, each with technologies, date and repository links."
      />
      <section className="py-20 md:py-24 px-6 bg-ink">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-cyber block mb-2"><EditableText k="projects_label" defaultValue="// PUBLISHED LABS" /></span>
              <h1 className="font-display text-3xl sm:text-5xl text-white font-bold uppercase"><EditableText k="projects_title" defaultValue="Active Projects" /></h1>
            </div>
            {!loading && !error && (
              <div className="font-mono text-xs text-dim" data-testid="projects-count">
                {projects.length}{" "}
                {projects.length === 1 ? (
                  <EditableText k="projects_count_singular" defaultValue="published repo on oVu-Lab" />
                ) : (
                  <EditableText k="projects_count_plural" defaultValue="published repos on oVu-Lab" />
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="font-mono text-xs text-dim" data-testid="projects-loading">
              <EditableText k="projects_loading" defaultValue="$ loading published projects ..." />
            </div>
          ) : error ? (
            <div className="p-8 rounded-xl bg-panel border border-white/10 font-mono text-xs" data-testid="projects-error">
              <p className="text-cyber mb-2">
                <EditableText k="projects_error_cmd" defaultValue="$ fetch ~/projects/published" />
              </p>
              <p className="text-red-400">
                <EditableText k="projects_error_msg" defaultValue="> request failed. Please reload the page." />
              </p>
            </div>
          ) : projects.length === 0 ? (
            <div className="p-10 rounded-xl bg-panel border border-white/10 font-mono text-xs max-w-2xl" data-testid="projects-empty-state">
              <p className="text-cyber mb-3">
                <EditableText k="projects_empty_cmd" defaultValue="$ ls ~/projects/published" />
              </p>
              <p className="text-white text-sm font-bold mb-2"><EditableText k="projects_empty_1" multiline defaultValue="> 0 entries — the lab is still cooking." /></p>
              <p className="text-dim leading-relaxed mb-4">
                <EditableText k="projects_empty_2" multiline defaultValue="I only publish projects here once they actually work and I can explain every line. Drafts and half-finished experiments stay private in my workspace until they earn their place." />
              </p>
              <p className="text-dim">
                <EditableText k="projects_empty_3" defaultValue="Check back soon — or watch progress on" />{" "}
                <a href={site.github_url} target="_blank" rel="noopener noreferrer" className="text-cyber underline" data-testid="projects-empty-github-link">
                  <EditableText k="projects_empty_github_link" defaultValue="GitHub" />
                </a>.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
              {projects.map((p) => (
                <ProjectCard key={p.id} project={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
